'use client';

import { useTranslations } from 'next-intl';
import { BentoSection1 } from 'components/ring-air-landing';
import {
  BottomBarAlternate,
  ProductSelectionSectionAlternate,
  TwoImageInfoSection,
} from 'components/ring-air';
import { BGImageTextSection } from 'components/ring-air/BGImageTextSection';
import { LifeStyle } from 'components/ring-air/Lifestyle';
import { Contents } from 'components/ring-air/Contents';
import { RingSelectionContextProvider } from 'components/ring-air/RingSelectionContext';
import { JsonLd } from 'components/shared';
import { TechnicalSpecs } from 'components/technicalSpecs/TechnicalSpecs';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import {
  AirRingColors,
  MiscRingCheckoutTypes,
  RingSizes,
} from 'content/products/ring';
import { getRingBuyPageFaqData } from 'content/products/ring/faqData';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { FAQSection } from 'components/shared/FAQSection';
import { ShippingEstimateObject } from 'components/ring-air/utils/types';
import { MasterTimelineProvider } from 'contexts/MasterTimelineContext';
import { getRingPricingSchemas } from 'lib/seo/meta/pricing/ring';

const RingBuy = ({
  defaultSize,
  defaultColor,
  defaultUhx,
  estimates,
  store,
  country,
}: {
  defaultSize: RingSizes | null;
  defaultColor: AirRingColors | null;
  defaultUhx: boolean;
  estimates?: ShippingEstimateObject;
  store: ShopifyStore;
  country: string | null;
}) => {
  const regionUpper = (country || '').toUpperCase();
  const isUSOrPR = regionUpper === 'US' || regionUpper === 'PR';
  const t = useTranslations('ring');
  const searchParams = useSearchParams();
  const darkMoonSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchParams?.get('section') === 'dark-moon') {
      setTimeout(() => {
        if (darkMoonSectionRef.current) {
          darkMoonSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 300);
    }
  }, []);

  const ringSchemas = getRingPricingSchemas({
    locale: 'us',
    country: 'us',
    store,
    currency: Currency.usd,
    defaultColor: defaultColor ?? AirRingColors.RAW_TITANIUM,
    t,
  });

  return (
    <>
      <JsonLd data={ringSchemas} />

      <RingSelectionContextProvider
        directCheckout={true}
        currency={Currency.usd}
        defaultSize={defaultSize}
        defaultColor={defaultColor ?? AirRingColors.RAW_TITANIUM}
        defaultUhx={defaultUhx}
        misc={MiscRingCheckoutTypes.BETTER_HELP_CHECKOUT}
        estimates={estimates}
        notServicable={isUSOrPR}
        notServicableMessage="We're currently unable to sell or ship this product to U.S. addresses. We'll update you here if that changes."
      >
        {/* Use Sections here make under /components/ring-pricing/your sections */}
        {/* from those - return <section></section> (styled) apply {gridMixin} in those and .content inside that will be grid-column: 2/-2 */}
        <ProductSelectionSectionAlternate
          misc={MiscRingCheckoutTypes.BETTER_HELP_CHECKOUT}
        />
        <Contents />
        <BGImageTextSection />
        <TwoImageInfoSection />
        <LifeStyle />
        <BentoSection1 />
        <TechnicalSpecs />
        <FAQSection faqData={getRingBuyPageFaqData(t)} />
        <BottomBarAlternate misc={MiscRingCheckoutTypes.BETTER_HELP_CHECKOUT} />
      </RingSelectionContextProvider>
    </>
  );
};

const Page = (props: any) => (
  <MasterTimelineProvider>
    <RingBuy {...props} />
  </MasterTimelineProvider>
);

export default Page;
